import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import DestinationCard from "@/components/site/DestinationCard";
import { Search, SlidersHorizontal, X } from "lucide-react";

const CATEGORIES = [
  { k: "all", l: "All" },
  { k: "Adventure", l: "Adventure" },
  { k: "Honeymoon", l: "Honeymoon" },
  { k: "Group", l: "Group Tours" },
  { k: "Corporate", l: "Corporate" },
  { k: "International", l: "International" },
];

const PRICE_BANDS = [
  { k: "any", l: "Any Price", min: null, max: null },
  { k: "budget", l: "Under ₹10k", min: null, max: 10000 },
  { k: "mid", l: "₹10k – ₹20k", min: 10000, max: 20000 },
  { k: "premium", l: "₹20k+", min: 20000, max: null },
];

export default function PackagesPage({ forceCategory }) {
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [category, setCategory] = useState(forceCategory || params.get("category") || "all");
  const [q, setQ] = useState(params.get("q") || "");
  const [band, setBand] = useState("any");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [priceMax, setPriceMax] = useState(35000);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const activeBand = PRICE_BANDS.find(b => b.k === band);
    const p = {};
    if (q) p.q = q;
    if (category && category !== "all") p.category = category;
    if (band !== "any") {
      if (activeBand.min != null) p.min_price = activeBand.min;
      if (activeBand.max != null) p.max_price = activeBand.max;
    } else if (showAdvanced) {
      p.max_price = priceMax;
    }
    api.get("/packages", { params: p })
      .then(r => setPackages(r.data.packages))
      .finally(() => setLoading(false));
  }, [category, q, band, priceMax, showAdvanced]);

  const clearAll = () => { setCategory(forceCategory || "all"); setQ(""); setBand("any"); setPriceMax(35000); };
  const hasFilters = category !== (forceCategory || "all") || q || band !== "any";

  return (
    <div className="pt-32 pb-24 bg-slate-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-semibold">Curated Journeys</div>
          <h1 className="mt-3 text-4xl sm:text-6xl font-bold text-blue-950 dark:text-white">{forceCategory ? `${forceCategory} Tours` : "All Packages"}</h1>
          <p className="mt-3 text-slate-600 dark:text-slate-300 max-w-2xl">Handpicked experiences across India &amp; the world — every stay, sight, and sunset planned to perfection.</p>
        </div>

        {/* Category Tabs — horizontal scrollable */}
        {!forceCategory && (
          <div data-testid="category-tabs" className="mb-6 -mx-6 lg:-mx-10 px-6 lg:px-10 overflow-x-auto no-scrollbar">
            <div className="flex gap-2 min-w-max pb-1">
              {CATEGORIES.map(c => (
                <button
                  key={c.k}
                  data-testid={`cat-tab-${c.k}`}
                  onClick={() => setCategory(c.k)}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap ${
                    category === c.k
                      ? "btn-gold"
                      : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 hover:border-[#D4AF37]"
                  }`}
                >{c.l}</button>
              ))}
            </div>
          </div>
        )}

        {/* Deal / price-band chips */}
        <div data-testid="price-chips" className="mb-6 flex flex-wrap gap-2">
          {PRICE_BANDS.map(b => (
            <button
              key={b.k}
              data-testid={`band-${b.k}`}
              onClick={() => setBand(b.k)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold ${
                band === b.k
                  ? "bg-blue-950 dark:bg-white text-white dark:text-blue-950"
                  : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:border-blue-950"
              }`}
            >{b.l}</button>
          ))}
        </div>

        {/* Search + Advanced toggle */}
        <div className="rounded-3xl p-4 mb-8 flex flex-col lg:flex-row gap-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 shadow-sm">
          <div className="flex-1 flex items-center gap-3 px-2">
            <Search className="w-4 h-4 text-slate-400"/>
            <input data-testid="filter-search" value={q} onChange={(e)=>setQ(e.target.value)}
              placeholder="Search Kashmir, Kerala, Bhutan..."
              className="w-full py-2.5 bg-transparent focus:outline-none text-sm"/>
          </div>
          <button data-testid="toggle-advanced" onClick={() => setShowAdvanced(v => !v)}
            className="px-4 py-2 rounded-full text-xs font-semibold border border-slate-200 dark:border-white/10 flex items-center gap-2">
            <SlidersHorizontal className="w-3.5 h-3.5"/> Advanced
          </button>
          {hasFilters && (
            <button data-testid="clear-filters" onClick={clearAll}
              className="px-4 py-2 rounded-full text-xs font-semibold text-[#D4AF37] flex items-center gap-1">
              <X className="w-3.5 h-3.5"/> Clear
            </button>
          )}
        </div>

        {showAdvanced && band === "any" && (
          <div className="rounded-2xl p-5 mb-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 flex items-center gap-4">
            <span className="text-xs uppercase tracking-widest text-slate-500">Max Price ₹</span>
            <input data-testid="filter-price" type="range" min="4000" max="35000" step="500" value={priceMax}
              onChange={(e)=>setPriceMax(+e.target.value)} className="flex-1 max-w-md"/>
            <span className="text-sm font-semibold text-blue-950 dark:text-white">₹{priceMax.toLocaleString('en-IN')}</span>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_,i) =>
              <div key={i} className="h-[520px] rounded-3xl bg-slate-100 dark:bg-slate-900 animate-pulse"/>
            )}
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-slate-500" data-testid="results-count">
              {packages.length} {packages.length === 1 ? "package" : "packages"} found
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {packages.map((p) => <DestinationCard key={p.slug} p={p}/>)}
            </div>
            {packages.length === 0 && (
              <div data-testid="packages-empty" className="text-center py-20">
                <div className="text-6xl">✦</div>
                <div className="mt-4 text-slate-500">No packages match your filters. Try widening your search.</div>
                <button onClick={clearAll} className="mt-4 px-6 py-2 rounded-full btn-gold text-xs">Reset filters</button>
              </div>
            )}
          </>
        )}
      </div>
      <style>{`.no-scrollbar::-webkit-scrollbar{display:none} .no-scrollbar{scrollbar-width:none}`}</style>
    </div>
  );
}
